import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { HeroSectionProps } from '../../types';
import { typography, layout, colors } from '../../styles/designTokens';
import VacuumEffect from '../VacuumEffect/VacuumEffect';

// מילים מתחלפות - כל
//  מילה עם אותו צבע כתום כמו "מישהו הסתפר פה"
const rotatingWords = [
  { text: 'נקיה', color: '#F5B658' },    // כתום - כמו "מישהו הסתפר פה"
  { text: 'נעימה', color: '#F5B658' },  // כתום - כמו "מישהו הסתפר פה"
  { text: 'חדשנית', color: '#F5B658' }  // כתום - כמו "מישהו הסתפר פה"
];

const HeroSection: React.FC<HeroSectionProps> = ({ logoImage }) => {
  // אנימציות בכניסה לדף - לא scroll animation כי זה בראש הדף
  const [isVisible, setIsVisible] = useState(false);
  const [isInView, setIsInView] = useState(true); // עוקב אחרי נראות הסקשן - לעצירת אנימציות
  const subtitleRef = useRef<HTMLHeadingElement>(null);
  const sectionRef = useRef<HTMLElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  
  // משפטים - בשורה אחת עם מילה מתחלפת
  const [showFirstSentence, setShowFirstSentence] = useState(false);
  
  // Typewriter effect - כתיבה ומחיקה
  const [wordIndex, setWordIndex] = useState(0);
  const [subIndex, setSubIndex] = useState(0); // כמה אותיות מוצגות
  const [isDeleting, setIsDeleting] = useState(false); // האם מוחקים עכשיו
  const [hasStarted, setHasStarted] = useState(false); // האם התחיל ה-typewriter
  const [isWaitingForNewWord, setIsWaitingForNewWord] = useState(false); // המתנה לפני מילה חדשה
  const [isVacuuming, setIsVacuuming] = useState(false); // האם השאיבה פעילה - לעצור את ה-typewriter!
  const [hasPulseAnimated, setHasPulseAnimated] = useState(false); // האם אפקט הפעימה כבר רץ
  
  // Memoization - למניעת re-renders מיותרים
  const currentWord = useMemo(() => rotatingWords[wordIndex], [wordIndex]);
  const displayText = useMemo(() => currentWord.text.substring(0, subIndex), [currentWord, subIndex]);
  
  // המתנה של 1 שניה בהתחלה לפני שה-typewriter מתחיל
  useEffect(() => {
    if (!showFirstSentence || hasStarted) return;
    
    setIsWaitingForNewWord(true);
    const initialDelay = setTimeout(() => {
      setHasStarted(true);
      setIsWaitingForNewWord(false);
    }, 1000); // 1 שניה המתנה - המשפט "איירי- חווית תספורת" יופיע לבד לפני שהמילה "נקיה" מתחילה להיכתב
    
    return () => clearTimeout(initialDelay);
  }, [showFirstSentence, hasStarted]);
  

  
  // אפקט Typewriter - כתיבה ומחיקה אות אחרי אות - עם חיבור לשאיבה
  useEffect(() => {
    // לא מתחילים עד שעברו 2 שניות, ועוצרים בזמן שאיבה!
    if (!showFirstSentence || !hasStarted || isVacuuming || !isInView || isWaitingForNewWord) return;
    
    const currentWordText = currentWord.text;
    
    // סיים לכתוב - מחכה ואז מתחיל למחוק
    if (subIndex === currentWordText.length && !isDeleting) {
      // שליחת אות - מילה נכתבה! (לפני ההמתנה)
      const wordCompleteEvent = new CustomEvent('wordComplete', { detail: { wordIndex } });
      window.dispatchEvent(wordCompleteEvent);
      
      // בדיקה: אם זה המילה השלישית (wordIndex === 2) - שאיבה אחרי המילה!
      if (wordIndex === 2) {
        // המתנה קטנה (100ms) כדי שהמילה "חדשנית" תופיע על המסך לפני השאיבה!
        setTimeout(() => {
          // שליחת אות ל-VacuumEffect להתחיל שאיבה!
          const vacuumEvent = new CustomEvent('startVacuum');
          window.dispatchEvent(vacuumEvent);
          
          // עצירת ה-Typewriter זמנית
          setIsVacuuming(true);
        }, 100); // 100ms - כדי שהמילה תופיע!
        
        // איפוס מיידי של הטקסט (כדי שלא יראו "חדשנית" כשהטקסט חוזר)
        setTimeout(() => {
          setWordIndex(0); // איפוס למילה הראשונה - "נקיה"
          setSubIndex(0);  // איפוס לתחילת המילה
          setIsDeleting(false); // התחל לכתוב מהתחלה
        }, 4300); // רגע לפני שהטקסט חוזר (שונה מ-6400 ל-4300 - סנכרון עם VacuumEffect)
        
        // אחרי 4.6 שניות - חזור ל-Typewriter (שונה מ-6700 ל-4600 - סנכרון עם VacuumEffect)
        setTimeout(() => {
          setIsVacuuming(false);
          // המתנה של שניה לפני שמתחילים לכתוב "נקיה" שוב
          setIsWaitingForNewWord(true);
          setTimeout(() => {
            setIsWaitingForNewWord(false);
          }, 1000);
        }, 4600);
        
        return;
      }
      
      const waitTimeout = setTimeout(() => setIsDeleting(true), 500); // 0.5 שנייה המתנה
      return () => clearTimeout(waitTimeout);
    }
    
    // סיים למחוק - עובר למילה הבאה
    if (subIndex === 0 && isDeleting) {
      setIsDeleting(false);
      const nextIndex = (wordIndex + 1) % rotatingWords.length;
      setWordIndex(nextIndex);
      
      // אם חוזרים למילה הראשונה ("נקיה") - המתנה של שניה
      if (nextIndex === 0) {
        setIsWaitingForNewWord(true);
        setTimeout(() => {
          setIsWaitingForNewWord(false);
        }, 1000);
      }
      return;
    }
    
    // כתיבה או מחיקה - מאוזן לביצועים
    const typingSpeed = isDeleting ? 60 : 100; // מאוזן - פחות עומס על הדפדפן
    const timeout = setTimeout(() => {
      setSubIndex((prev) => prev + (isDeleting ? -1 : 1));
    }, typingSpeed);
    
    return () => clearTimeout(timeout);
  }, [subIndex, isDeleting, wordIndex, showFirstSentence, hasStarted, isVacuuming, isInView, isWaitingForNewWord, currentWord]);

  // הפעלת אנימציות בכניסה לדף
  useEffect(() => {
    // Delay קצר כדי שהדף יטען קודם
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  // הוספת classes בהתאם ל-isVisible
  useEffect(() => {
    // הוספת base classes מיד (כדי שהאלמנטים יהיו מוכנים לאנימציה)
    // עם !important ב-CSS, האלמנטים יתחילו בלתי נראים
    if (subtitleRef.current) {
      subtitleRef.current.classList.add('scroll-slide-in-right');
    }
  }, []);


  // הוספת visible class כש-isVisible=true - הכל ביחד ללא delays
  useEffect(() => {
    if (isVisible) {
      // כותרת משנה - המשפט הראשון ("חווית תספורת") - ביחד עם איירי
      setShowFirstSentence(true);
      if (subtitleRef.current) {
        subtitleRef.current.classList.add('visible');
      }
    }
  }, [isVisible]);

  // זיהוי האם הסקשן בפריים - לעצירת כל האנימציות כשיוצאים
  useEffect(() => {
    if (!sectionRef.current || typeof IntersectionObserver === 'undefined') return;

    const observer = new IntersectionObserver(
      ([entry]) => setIsInView(entry.isIntersecting),
      { threshold: 0.1 } // 10% מהסקשן נראה = פעיל
    );

    observer.observe(sectionRef.current);

    return () => observer.disconnect();
  }, []);











  return (
    <>
      <section 
        ref={sectionRef}
        id="home" 
        aria-label="מסך פתיחה - מכונת תספורת AiRY"
        className="relative flex items-start overflow-hidden" 
      style={{
        // ריווח אנכי מינימלי מאוד מאוד - הקטנה דרסטית של השטח השחור המיותר
        // Responsive: במובייל ובטאבלט - 0px-5px, בדסקטופ - 5px-15px
        overflowX: 'hidden', // מונע overflow אופקי
        width: '100%',
        maxWidth: '100%',
        paddingTop: 'clamp(0px, 1vw, 15px)', // 0px במובייל, 15px בדסקטופ (מינימלי מאוד מאוד)
        paddingBottom: 'clamp(0px, 1vw, 15px)', // 0px במובייל, 15px בדסקטופ (מינימלי מאוד מאוד)
        marginBottom: '0px', // ללא margin שלילי - כדי ש"החזון שלנו" יהיה גלוי
        marginTop: '0px', // ללא margin למעלה
        minHeight: 'clamp(500px, 100vh, calc(100vh - 65px))', // מובייל: 500px, דסקטופ: מסך מלא
        position: 'relative',
        height: 'auto', // גובה דינמי
        backgroundColor: '#000000', // רקע שחור
        }}
    >
      <div className="container mx-auto px-4 relative z-30" style={{ maxWidth: layout.contentMaxWidth, paddingBottom: '0' }}>
        <div ref={contentRef} className="flex flex-col md:flex-row relative" style={{ gap: 'clamp(10px, 2vw, 20px)', paddingBottom: '0', marginBottom: '0', width: '100%' }}>
          {/* Right Side - Main Headline - Responsive - בצד ימין לגמרי */}
          <div className="text-right flex flex-col" style={{ 
            paddingTop: '0', 
            marginTop: 'clamp(20px, 3vw, 40px)', // הורדת המלל למטה - הוספת marginTop
            paddingBottom: '0',
            marginBottom: '0',
            gap: '0',
            position: 'absolute',
            right: 'clamp(20px, 6vw, 110px)', // מובייל: 20px, דסקטופ: 110px
            top: 'clamp(100px, 12vw, 150px)', // מובייל: 100px, דסקטופ: 150px
            zIndex: 20, // מעל המכונה והחלקיקים!
          }}>
            {/* Main Headline - שורה אחת עם מילה מתחלפת */}
            <h1
              ref={subtitleRef}
              className="hero-subtitle font-black leading-tight"
              style={{ 
                fontSize: 'clamp(1.75rem, 7vw, 4.2rem)', // מובייל: 28px, דסקטופ: 67px
                fontFamily: typography.heading.fontFamily, // Rubik לכותרות - כמו של ירושלים ובני ברק
                lineHeight: '1.2', // חזרה ל-1.2 - רווח נוח יותר
                letterSpacing: typography.heading.letterSpacing,
                color: colors.textGold.base, // צבע זהב בסיס לטקסט רגיל
                marginBottom: '0',
                paddingBottom: '0',
                marginTop: '0',
                paddingTop: '0',
                minHeight: 'clamp(150px, 20vw, 250px)', // 🔧 CLS FIX: שומר מקום מראש כדי שהאנימציה pulseIn לא תדחוף אלמנטים למטה
              }}
            >
              {/* איירי בשורה נפרדת, ומתחתיו המשפט עם מילה מתחלפת */}
              {showFirstSentence && (
                <div 
                  className={`first-sentence-slide-in${hasPulseAnimated ? ' already-animated' : ''}`}
                  onAnimationEnd={() => setHasPulseAnimated(true)}
                  style={{ 
                    direction: 'rtl',
                    textAlign: 'right',
                    opacity: showFirstSentence ? 1 : 0,
                    filter: 'none',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.5rem',
                    alignItems: 'flex-end',
                    lineHeight: '1.2',
                    marginBottom: '0',
                    transition: 'opacity 0.8s ease-out',
                  }}
                >
                  {/* איירי - בשורה נפרדת למעלה - מיושר ימינה עם מקף */}
                  <div style={{ color: '#FFFFFF', textAlign: 'right', width: '100%' }}>איירי-</div>
                  
                  {/* חווית תספורת + המילה המתחלפת - בשורה שנייה */}
                  <div style={{ display: 'flex', gap: '1rem', alignItems: 'baseline', justifyContent: 'flex-end', flexWrap: 'nowrap', whiteSpace: 'nowrap' }}>
                    <span style={{ color: '#FFFFFF' }}>חווית תספורת</span>
                    
                    {/* המילה המתחלפת - Typewriter effect עם צבע כתום */}
                    <span 
                      style={{
                        display: 'inline-block',
                        // ⚡ CLS FIX: minWidth קבוע תמיד (לא משתנה!) - מונע Layout Shift בזמן Typewriter
                        // סיבה: שומר מקום מראש למילה הכי ארוכה ("חדשנית") = אלמנטים לא קופצים
                        minWidth: 'clamp(100px, 18vw, 160px)', // 🎯 UPDATED: הגדלה ל-160px כדי למנוע קפיצות
                        color: currentWord.color,
                        transition: 'min-width 0.1s ease-in-out',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {displayText}
                      {subIndex === currentWord.text.length && '.'}
                    </span>
                  </div>
                </div>
              )}
            </h1>
          </div>
        
        </div>
      </div>
      
      {/* VacuumEffect - אפקט השאיבה - עם שליטה על נראות */}
      <VacuumEffect isInView={isInView} />
    </section>
    </>
  );
};

export default HeroSection;
